import { useMemo } from 'react';
import {
  type RegionConfig,
  resolveBuildRegion,
  resolveRegion,
} from '../regions/regionConfig';

/**
 * useRegion — returns the active RegionConfig for the current hostname.
 *
 * Resolution order:
 *   1. Build-time `VITE_HOST` (when set during `pnpm build` for per-region SSG)
 *   2. Runtime `window.location.host` (dev server, preview, deployed runtime)
 *   3. AU fallback (per BR-C-F14A-030)
 *
 * Memoised — the resolved region doesn't change within a render, and the hook
 * itself returns a stable object reference across renders to keep downstream
 * effect deps clean. Per F1-14A § 22 + code-handoff brief v2.2 § 12.3.
 *
 * Usage:
 *   const region = useRegion();
 *   <a href={`tel:${region.phone.tel}`}>{region.phone.display}</a>
 */
export function useRegion(): RegionConfig {
  return useMemo(() => {
    // Build-time first — vite-react-ssg's static render pass evaluates the
    // component tree with import.meta.env populated and no `window`. The
    // baked HTML for `dist/sg/index.html` therefore contains SG content.
    if (typeof window === 'undefined') return resolveBuildRegion();
    // Runtime — overrides build-time when the deployed App Service serves
    // the same SPA on multiple hosts. The build-time choice still wins for
    // SEO-critical baked HTML; runtime resolution refines for in-app
    // navigation and any deferred fetches.
    return resolveRegion(window.location.host);
  }, []);
}
